import React, { useState } from 'react';
import { FaFilter, FaTimes, FaCheck } from 'react-icons/fa';

const ProductFilter = ({ filters, onFilterChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  const ramOptions = ['4GB', '6GB', '8GB', '12GB', '16GB'];
  const storageOptions = ['64GB', '128GB', '256GB', '512GB', '1TB'];
  const conditionOptions = ['new', 'used', 'open_box'];
  const priceRanges = [
    { label: 'Under PKR 50,000', min: 0, max: 50000 },
    { label: 'PKR 50,000 - 100,000', min: 50000, max: 100000 },
    { label: 'PKR 100,000 - 200,000', min: 100000, max: 200000 },
    { label: 'Above PKR 200,000', min: 200000, max: null },
  ];

  const handleFilterChange = (key, value) => {
    onFilterChange({ [key]: value });
  };

  const handlePriceRangeChange = (range) => {
    onFilterChange({
      minPrice: range.min,
      maxPrice: range.max || '',
    });
  };

  const clearFilters = () => {
    onFilterChange({
      brand: '',
      category: '',
      minPrice: '',
      maxPrice: '',
      ram: '',
      storage: '',
      condition: '',
      search: '',
      sortBy: 'newest',
    });
  };

  // Only consider actual filter fields (not sortBy default or page)
  const filterKeys = ['brand', 'category', 'minPrice', 'maxPrice', 'ram', 'storage', 'condition', 'search'];
  const hasActiveFilters = filterKeys.some(key => {
    const v = filters[key];
    return v !== '' && v !== null && v !== undefined && v !== 0;
  });

  return (
    <>
      {/* Mobile Filter Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="lg:hidden fixed bottom-6 right-6 z-40 bg-primary-600 text-white w-12 h-12 rounded-full shadow-lg flex items-center justify-center"
      >
        <FaFilter />
      </button>

      {/* Filter Sidebar */}
      <div className={`${isOpen ? 'fixed inset-0 z-50' : 'relative'} lg:static`}>
        {/* Overlay */}
        {isOpen && (
          <div className="fixed inset-0 bg-black/50" onClick={() => setIsOpen(false)} />
        )}

        {/* Filter Content */}
        <div className={`${isOpen ? 'fixed right-0 top-0 h-full w-80 bg-white dark:bg-gray-900 shadow-xl overflow-y-auto' : 'relative'} p-6`}>
          <div className="flex justify-between items-center mb-6 lg:hidden">
            <h3 className="text-xl font-semibold">Filters</h3>
            <button onClick={() => setIsOpen(false)}>
              <FaTimes />
            </button>
          </div>

          <h3 className="text-xl font-semibold mb-6 hidden lg:block">Filters</h3>

          {/* Price Range */}
          <div className="mb-6">
            <h4 className="font-semibold mb-3">Price Range</h4>
            <div className="space-y-2">
              {priceRanges.map((range, idx) => (
                <label key={idx} className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative w-4 h-4 flex items-center justify-center">
                    <input
                      type="radio"
                      name="priceRange"
                      checked={filters.minPrice == range.min && filters.maxPrice == (range.max || '')}
                      onChange={() => handlePriceRangeChange(range)}
                      className="appearance-none w-4 h-4 border-2 border-gray-400 rounded-full cursor-pointer checked:bg-primary-600 checked:border-primary-600"
                    />
                    {filters.minPrice == range.min && filters.maxPrice == (range.max || '') && (
                      <FaCheck className="absolute w-2.5 h-2.5 text-white pointer-events-none" />
                    )}
                  </div>
                  <span className="text-sm group-hover:text-primary-600 transition-colors">{range.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* RAM */}
          <div className="mb-6">
            <h4 className="font-semibold mb-3">RAM</h4>
            <div className="space-y-2">
              {ramOptions.map(ram => (
                <label key={ram} className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative w-4 h-4 flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={filters.ram === ram}
                      onChange={() => handleFilterChange('ram', filters.ram === ram ? '' : ram)}
                      className="appearance-none w-4 h-4 border-2 border-gray-400 rounded cursor-pointer checked:bg-primary-600 checked:border-primary-600"
                    />
                    {filters.ram === ram && (
                      <FaCheck className="absolute w-2.5 h-2.5 text-white pointer-events-none" />
                    )}
                  </div>
                  <span className="text-sm group-hover:text-primary-600 transition-colors">{ram}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Storage */}
          <div className="mb-6">
            <h4 className="font-semibold mb-3">Storage</h4>
            <div className="space-y-2">
              {storageOptions.map(storage => (
                <label key={storage} className="flex items-center gap-2 cursor-pointer group">
                  <div className="relative w-4 h-4 flex items-center justify-center">
                    <input
                      type="checkbox"
                      checked={filters.storage === storage}
                      onChange={() => handleFilterChange('storage', filters.storage === storage ? '' : storage)}
                      className="appearance-none w-4 h-4 border-2 border-gray-400 rounded cursor-pointer checked:bg-primary-600 checked:border-primary-600"
                    />
                    {filters.storage === storage && (
                      <FaCheck className="absolute w-2.5 h-2.5 text-white pointer-events-none" />
                    )}
                  </div>
                  <span className="text-sm group-hover:text-primary-600 transition-colors">{storage}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Condition */}
          <div className="mb-6">
            <h4 className="font-semibold mb-3">Condition</h4>
            <div className="space-y-2">
              {conditionOptions.map(condition => (
                <label key={condition} className="flex items-center gap-2 capitalize cursor-pointer group">
                  <div className="relative w-4 h-4 flex items-center justify-center">
                    <input
                      type="radio"
                      name="condition"
                      checked={filters.condition === condition}
                      onChange={() => handleFilterChange('condition', condition)}
                      className="appearance-none w-4 h-4 border-2 border-gray-400 rounded-full cursor-pointer checked:bg-primary-600 checked:border-primary-600"
                    />
                    {filters.condition === condition && (
                      <FaCheck className="absolute w-2.5 h-2.5 text-white pointer-events-none" />
                    )}
                  </div>
                  <span className="text-sm group-hover:text-primary-600 transition-colors">{condition}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="w-full mt-4 text-center text-sm text-primary-600 hover:text-primary-700"
            >
              Clear all filters
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default ProductFilter;